export type ShellTarget = "sage" | "rg";

export interface ShellConfig {
  target: ShellTarget;
  name: string;
  longName: string;
  eyebrow: string;
  themeColor: string;
  peerName: string;
  peerUrl: string;
}

export interface HealthPayload {
  ok: boolean;
  checked_at: string;
  registered_devices: number | null;
  dependencies: {
    core: Record<string, unknown> & { ok?: boolean; ts?: string };
    business: Record<string, unknown> & { ok?: boolean; ts?: string };
  };
}

export interface BootstrapPayload {
  scope: ShellTarget;
  generated_at: string;
  metrics: {
    registered_devices: number | null;
    recent_client_sessions: number | null;
    open_actions: number | null;
  };
  brief: null | {
    date: string;
    generated_at: string;
    message_count: number;
    content_locked: boolean;
  };
}

export interface DeviceProfile {
  deviceId: string;
  installationId: string;
  appScope: ShellTarget;
  label: string;
  platform: string;
  locale: string;
  timezone: string;
  userAgent: string;
  displayMode: string;
  capabilities: Record<string, boolean>;
}

export interface CaptureItem {
  id: string;
  text: string;
  scope: ShellTarget;
  createdAt: string;
  status: "pending_review" | "approved_for_draft" | "archived";
  requires_evan_approval: 1;
  source: "typed" | "voice";
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

const DB_NAME = "sage-shell-local";
const DB_VERSION = 1;

export const shellConfig: ShellConfig = __SHELL_TARGET__ === "rg" ? {
  target: "rg",
  name: "R&G",
  longName: "R&G Shell",
  eyebrow: "Restaurant consulting field OS",
  themeColor: "#2f463c",
  peerName: "SAGE Shell",
  peerUrl: "https://sage-shell.pages.dev",
} : {
  target: "sage",
  name: "SAGE",
  longName: "SAGE Shell",
  eyebrow: "Personal intelligence OS",
  themeColor: "#2e4928",
  peerName: "R&G Shell",
  peerUrl: "https://rg-shell-v2.pages.dev",
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("settings")) db.createObjectStore("settings");
      if (!db.objectStoreNames.contains("captures")) db.createObjectStore("captures", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbGet<T>(storeName: string, key: IDBValidKey): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readonly");
    const request = transaction.objectStore(storeName).get(key);
    request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
}

async function idbPut(storeName: string, value: unknown, key?: IDBValidKey): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    key === undefined ? store.put(value) : store.put(value, key);
    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
  });
}

export async function getSetting(key: string): Promise<string> {
  return (await idbGet<string>("settings", key)) || "";
}

export async function setSetting(key: string, value: string): Promise<void> {
  await idbPut("settings", value, key);
}

export async function getCaptures(): Promise<CaptureItem[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("captures", "readonly");
    const request = transaction.objectStore("captures").getAll();
    request.onsuccess = () => resolve((request.result as CaptureItem[]).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
}

export async function saveCapture(item: CaptureItem): Promise<void> {
  await idbPut("captures", item);
}

function displayMode(): string {
  if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
  if ((navigator as Navigator & { standalone?: boolean }).standalone) return "standalone";
  return "browser";
}

export async function ensureDeviceProfile(target: ShellTarget): Promise<DeviceProfile> {
  let deviceId = localStorage.getItem("sage_device_id") || await idbGet<string>("settings", "sage_device_id");
  let installationId = localStorage.getItem("sage_installation_id") || await idbGet<string>("settings", "sage_installation_id");
  if (!deviceId) deviceId = crypto.randomUUID();
  if (!installationId) installationId = crypto.randomUUID();
  localStorage.setItem("sage_device_id", deviceId);
  localStorage.setItem("sage_installation_id", installationId);
  await idbPut("settings", deviceId, "sage_device_id");
  await idbPut("settings", installationId, "sage_installation_id");

  const nav = navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } };
  const capabilities = {
    has_camera: Boolean(navigator.mediaDevices?.getUserMedia),
    has_microphone: Boolean(navigator.mediaDevices?.getUserMedia),
    has_geolocation: "geolocation" in navigator,
    has_notifications: "Notification" in window,
    has_share: "share" in navigator,
    is_mobile: Boolean(nav.userAgentData?.mobile) || /Android|iPhone|iPad/i.test(navigator.userAgent),
    is_pwa_installed: displayMode() === "standalone",
    is_online: navigator.onLine,
  };

  return {
    deviceId,
    installationId,
    appScope: target,
    label: `${nav.userAgentData?.platform || navigator.platform || "Browser"} · ${target.toUpperCase()}`,
    platform: nav.userAgentData?.platform || navigator.platform || "unknown",
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
    displayMode: displayMode(),
    capabilities,
  };
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { ...init, headers: { "Content-Type": "application/json", ...init?.headers } });
  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
  if (!response.ok) throw new Error((body as { error?: string }).error || `HTTP ${response.status}`);
  return body as T;
}

export async function registerDevice(profile: DeviceProfile): Promise<{ registered_at: string }> {
  return fetchJson("/api/register-device", {
    method: "POST",
    body: JSON.stringify({
      device_id: profile.deviceId,
      installation_id: profile.installationId,
      app_scope: profile.appScope,
      device_label: profile.label,
      platform: profile.platform,
      locale: profile.locale,
      timezone: profile.timezone,
      user_agent: profile.userAgent,
      pwa_display_mode: profile.displayMode,
      capabilities: profile.capabilities,
    }),
  });
}

export function getHealth(): Promise<HealthPayload> {
  return fetchJson("/api/health");
}

export function getBootstrap(target: ShellTarget): Promise<BootstrapPayload> {
  return fetchJson(`/api/bootstrap?scope=${target}`);
}

function parseMcpEnvelope(text: string): unknown {
  const dataLines = text.split(/\r?\n/).filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim());
  const candidate = dataLines.at(-1) || text.trim();
  return candidate ? JSON.parse(candidate) : null;
}

async function mcpPost(scope: "core" | "business", token: string, payload: Record<string, unknown>, sessionId?: string) {
  const result = await fetchJson<{ ok: boolean; status: number; body: string; session_id?: string }>("/api/mcp", {
    method: "POST",
    body: JSON.stringify({ scope, token, payload, session_id: sessionId }),
  });
  return { ...result, parsed: parseMcpEnvelope(result.body) };
}

export async function probeMcp(scope: "core" | "business", token: string): Promise<{ sessionId: string | null; server: unknown }> {
  const id = crypto.randomUUID();
  const initialized = await mcpPost(scope, token, {
    jsonrpc: "2.0",
    id,
    method: "initialize",
    params: {
      protocolVersion: "2025-03-26",
      capabilities: {},
      clientInfo: { name: "sage-shell", version: "1.0.0" },
    },
  });
  if (initialized.session_id) {
    await mcpPost(scope, token, { jsonrpc: "2.0", method: "notifications/initialized", params: {} }, initialized.session_id);
  }
  return { sessionId: initialized.session_id || null, server: initialized.parsed };
}

export async function sendChat(input: {
  apiKey: string;
  model: string;
  target: ShellTarget;
  messages: ChatMessage[];
  coreToken: string;
  businessToken: string;
}): Promise<string> {
  const result = await fetchJson<{ content?: Array<{ type?: string; text?: string }>; error?: { message?: string } }>("/api/chat", {
    method: "POST",
    body: JSON.stringify({
      api_key: input.apiKey,
      model: input.model,
      scope: input.target,
      messages: input.messages.map(({ role, content }) => ({ role, content })),
      core_mcp_token: input.coreToken,
      business_mcp_token: input.businessToken,
    }),
  });
  if (result.error?.message) throw new Error(result.error.message);
  const text = (result.content || []).filter((block) => block.type === "text" && block.text).map((block) => block.text).join("\n\n");
  return text || "The model returned tool activity but no final text. Try asking it to summarize its result.";
}

export const buildTime = __BUILD_TIME__;
